import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Panel } from "./ui";
import { Button } from "@/components/ui/button";
import { ScaleIcon, ShieldAlert, Download, FileText } from "lucide-react";

const SEV = { info: "#0369a1", warn: "#b45309", critical: "#dc2626" };

const downloadFile = async (path, filename) => {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const Big = ({ label, value, hint, color = "#0B2545" }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-5 text-center" style={{ borderTop: `3px solid ${color}` }}>
    <div className="overline text-slate-500">{label}</div>
    <div className="font-head text-4xl font-bold mt-2" style={{ color }}>{value}</div>
    {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
  </div>
);

const LABELS = {
  in_bin: "In Bin", in_transit_to_regional: "In transit to regional", at_regional: "At Regional", in_transit_to_site: "In transit to site", at_site: "At Site", installed: "Installed (awaiting IP2)",
  active: "Active", return_in_transit: "Return in transit", returned: "Returned", written_off: "Written off",
};

export default function Reconciliation() {
  const [d, setD] = useState(null);
  const [backfilled, setBackfilled] = useState([]);
  useEffect(() => {
    api.get("/reports/reconciliation").then((r) => setD(r.data));
    api.get("/reports/backfilled-activations").then((r) => setBackfilled(r.data)).catch(() => {});
  }, []);
  if (!d) return null;

  return (
    <div>
      <PageHeader title="AX Reconciliation" subtitle="Ledger vs the AX consignment bin balance (AX sees only IP1 issue and IP2 activation).">
        <Button data-testid="export-recon-csv" onClick={() => downloadFile("/reports/reconciliation.csv", "netstar_reconciliation.csv")} variant="outline" className="rounded-sm border-slate-300 font-head">
          <Download className="h-4 w-4 mr-2" /> Reconciliation CSV
        </Button>
        <Button data-testid="export-finance-pdf" onClick={() => downloadFile("/reports/finance.pdf", "netstar_finance_report.pdf")} className="rounded-sm bg-[#0B2545] hover:bg-[#0B2545]/90 font-head">
          <FileText className="h-4 w-4 mr-2" /> Finance PDF
        </Button>
      </PageHeader>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Big label="AX Bin Balance" value={d.ax_bin_balance} hint="IP1 issued − IP2 activated" color="#0B2545" />
        <Big label="Ledger — In Bin" value={d.ledger_in_bin} hint="physically in the main bin" color="#00857C" />
        <Big label="In Field (on consignment)" value={d.in_field} hint="regional + site + installed" color="#0369a1" />
        <Big label="Variance to Explain" value={d.variance} hint="AX bin − ledger in-bin" color={d.flagged ? "#dc2626" : "#0369a1"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Where the AX-counted units actually are">
          <table className="w-full text-sm" data-testid="recon-breakdown">
            <tbody>
              {Object.entries(d.breakdown).map(([k, v]) => (
                <tr key={k} className="border-b border-slate-100">
                  <td className="py-2 text-slate-600">{LABELS[k] || k}</td>
                  <td className="py-2 text-right font-head font-bold text-[#0B2545]">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-400 mt-3">Distribution to regions and sites is not visible to AX, so AX still counts those units in the bin — that is expected variance. Returns and write-offs are true variances to reconcile.</p>
        </Panel>

        <Panel title="Reconciliation Flags">
          {d.flags.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-emerald-600 py-6"><ScaleIcon className="h-5 w-5" /> Ledger reconciles cleanly with AX — no variances to action.</div>
          ) : (
            <div className="space-y-3" data-testid="recon-flags">
              {d.flags.map((f) => (
                <div key={f.type} className="flex gap-3 border border-slate-100 rounded-sm p-3" style={{ borderLeft: `3px solid ${SEV[f.severity]}` }}>
                  <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" style={{ color: SEV[f.severity] }} />
                  <div>
                    <div className="text-sm font-head font-semibold text-[#0B2545]">{f.type} <span className="font-label" style={{ color: SEV[f.severity] }}>· {f.count}</span></div>
                    <div className="text-xs text-slate-500">{f.note}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title="IP2 Back-Filled Activations — month-end sign-off" className="mt-6">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <p className="text-xs text-slate-400 max-w-2xl">Units activated by an AX IP2 notice where the receipt and/or fitment stage was never captured in-app (e.g. a Mobitech field scan). The ledger was auto-reconstructed; Finance should confirm custody and billing before sign-off.</p>
          {backfilled.length > 0 && (
            <Button data-testid="export-backfilled-csv" onClick={() => downloadFile("/reports/backfilled.csv", "netstar_backfilled_activations.csv")} variant="outline" size="sm" className="rounded-sm border-slate-300 font-head">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
          )}
        </div>
        {backfilled.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-600 py-6"><ScaleIcon className="h-5 w-5" /> No back-filled activations — every active unit followed the full receipt → fitment → IP2 trail.</div>
        ) : (
          <table className="w-full text-sm" data-testid="backfilled-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Serial</th><th className="py-2 pr-3">Product</th><th className="py-2 pr-3">Fitment Centre</th><th className="py-2 pr-3">Region</th><th className="py-2 pr-3">Channel</th><th className="py-2 pr-3">Work Order</th><th className="py-2 pr-3">Skipped</th><th className="py-2 pr-3">Age (days)</th></tr></thead>
            <tbody>
              {backfilled.map((r) => (
                <tr key={r.serial} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-label font-medium text-[#0B2545]">{r.serial}</td>
                  <td className="py-2 pr-3 text-slate-600">{r.product_name}</td>
                  <td className="py-2 pr-3 text-slate-600">{r.centre}</td>
                  <td className="py-2 pr-3 text-slate-500">{r.region}</td>
                  <td className="py-2 pr-3 text-slate-600">{r.channel}</td>
                  <td className="py-2 pr-3 text-slate-500 font-label">{r.work_order_no}</td>
                  <td className="py-2 pr-3">{(r.skipped_stages || []).map((s) => (
                    <span key={s} className="inline-block mr-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wide" style={{ background: "#fff7ed", color: "#b45309" }}>{s.replace(/_/g, " ")}</span>
                  ))}</td>
                  <td className="py-2 pr-3 font-head font-bold text-[#0B2545]">{r.age_days}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
